// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { observer } from "mobx-react-lite";

import { isEligibleOrAlmostEligible, Opportunity } from "../../WorkflowsStore";
import { Heading } from "./Heading";
import { AccordionSection, AccordionWrapper } from "./OpportunitiesAccordion";
import { OpportunityBanner } from "./OpportunityBanner";

type OpportunityOverviewProps = {
  opportunity: Opportunity;
  formLinkButton?: boolean;
  onDenialButtonClick?: () => void;
  justiceInvolvedPersonTitle: string;
  hideActionButtons?: boolean;
  shouldTrackOpportunityPreviewed?: boolean;
};

const OpportunityOverviewComponent: React.FC<OpportunityOverviewProps> = ({
  opportunity,
  formLinkButton,
  onDenialButtonClick,
  justiceInvolvedPersonTitle,
  hideActionButtons,
  shouldTrackOpportunityPreviewed = true,
}) => {
  const selectedPerson = opportunity.person;
  const showAccordion =
    isEligibleOrAlmostEligible(selectedPerson, opportunity.type) ||
    opportunity.config.supportsIneligible;

  return (
    <>
      <Heading person={selectedPerson} trackingOpportunity={opportunity} />
      {opportunity.bannerInfo && (
        <OpportunityBanner bannerInfo={opportunity.bannerInfo} />
      )}
      {showAccordion && (
        <AccordionWrapper
          allowZeroExpanded
          preExpanded={[opportunity.accordionKey]}
          key={opportunity.accordionKey}
        >
          <AccordionSection
            opportunity={opportunity}
            formLinkButton={formLinkButton}
            onDenialButtonClick={onDenialButtonClick}
            hideActionButtons={hideActionButtons}
            shouldTrackOpportunityPreviewed={shouldTrackOpportunityPreviewed}
          />
        </AccordionWrapper>
      )}
    </>
  );
};

export const OpportunityOverview = observer(OpportunityOverviewComponent);
